import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { db, functions } from '../../lib/firebase';
import { doc, getDoc, query, collection, where, limit, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Globe, Check, Loader, AlertCircle, X, Sparkles } from 'lucide-react';

export default function UrlAddonCheckout() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  
  const [selectedType, setSelectedType] = useState<'path' | 'subdomain'>('subdomain');
  const [username, setUsername] = useState('');
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasExistingAddon, setHasExistingAddon] = useState(false);

  useEffect(() => {
    const checkExisting = async () => {
      if (!user?.uid) return;
      
      try {
        const urlAddonDoc = await getDoc(doc(db, 'urlAddons', user.uid));
        if (urlAddonDoc.exists() && urlAddonDoc.data().status === 'active') {
          setHasExistingAddon(true);
        }
      } catch (err) {
        console.error('Error checking existing addon:', err);
      }
    };
    
    checkExisting();
  }, [user]);

  const validateUsername = async (usernameToCheck: string) => {
    if (!usernameToCheck || usernameToCheck.trim() === '') {
      setUsernameAvailable(null);
      setUsernameError(null);
      return;
    }

    setIsCheckingUsername(true);
    setUsernameAvailable(null);
    setUsernameError(null);

    const normalized = usernameToCheck.toLowerCase().trim();

    if (normalized.length < 3 || normalized.length > 30) {
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameError('ความยาว 3-30 ตัวอักษร');
      return;
    }

    if (!/^[a-z0-9_-]+$/.test(normalized)) {
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameError('ใช้ได้เฉพาะ a-z, 0-9, _, - เท่านั้น');
      return;
    }

    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('username', '==', normalized),
        limit(1)
      );
      const usersSnapshot = await getDocs(usersQuery);

      if (!usersSnapshot.empty) {
        setIsCheckingUsername(false);
        setUsernameAvailable(false);
        setUsernameError('ชื่อนี้ถูกใช้งานแล้ว');
        return;
      }

      const salePageQuery = query(
        collection(db, 'salePageSubscriptions'),
        where('username', '==', normalized),
        where('hasSubdomain', '==', true),
        limit(1)
      );
      const salePageSnapshot = await getDocs(salePageQuery);

      if (!salePageSnapshot.empty) {
        setIsCheckingUsername(false);
        setUsernameAvailable(false);
        setUsernameError('ชื่อนี้ถูกใช้งานแล้ว');
        return;
      }

      setIsCheckingUsername(false);
      setUsernameAvailable(true);
      setUsernameError(null);
    } catch (err: any) {
      console.error('Error checking username:', err);
      setIsCheckingUsername(false);
      setUsernameAvailable(false);
      setUsernameError('เกิดข้อผิดพลาดในการตรวจสอบ');
    }
  };

  useEffect(() => {
    if (!username) return;
    
    const timeoutId = setTimeout(() => {
      validateUsername(username);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [username]);

  const handlePurchase = async () => {
    if (!user || !username || !usernameAvailable) return;

    setIsPurchasing(true);
    setError(null);

    try {
      const purchaseUrlAddon = httpsCallable(functions, 'purchaseUrlAddon');
      const result = await purchaseUrlAddon({
        username: username.toLowerCase().trim(),
        type: selectedType,
        paymentId: 'MOCK_PAYMENT_ID',
        purchasedFrom: 'instructor'
      });

      navigate('/instructor/settings?tab=url&success=true');
    } catch (err: any) {
      console.error('Error purchasing URL addon:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการซื้อ URL Add-on');
    } finally {
      setIsPurchasing(false);
    }
  };

  if (hasExistingAddon) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              คุณมี URL Add-on อยู่แล้ว
            </h2>
            <p className="text-gray-600 mb-6">
              ไปที่หน้าการตั้งค่าเพื่อจัดการ URL ของคุณ
            </p>
            <button
              onClick={() => navigate('/instructor/settings?tab=url')}
              className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
            >
              ไปที่การตั้งค่า
            </button>
          </div>
        </div>
      </div>
    );
  }

  const price = selectedType === 'path' ? 49 : 99;
  const previewUrl = selectedType === 'subdomain' 
    ? `${username || 'username'}.edumi.co` 
    : `edumi.co/@${username || 'username'}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-500 to-cyan-600 bg-clip-text text-transparent mb-2">
            ซื้อ URL Add-on
          </h1>
          <p className="text-gray-600">
            เลือก URL ที่เหมาะกับคุณและสร้างแบรนด์ของคุณเอง
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div
            onClick={() => setSelectedType('path')}
            className={`bg-white rounded-2xl p-6 cursor-pointer transition-all ${
              selectedType === 'path'
                ? 'ring-4 ring-emerald-500 shadow-xl'
                : 'hover:shadow-lg'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Path URL</h3>
                <p className="text-sm text-gray-600">เหมาะสำหรับเริ่มต้น</p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedType === 'path'
                  ? 'border-emerald-500 bg-emerald-500'
                  : 'border-gray-300'
              }`}>
                {selectedType === 'path' && <Check className="w-4 h-4 text-white" />}
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <code className="text-sm text-gray-700">
                edumi.co/@username
              </code>
            </div>
            
            <div className="text-3xl font-bold text-gray-900 mb-4">
              49฿<span className="text-lg font-normal text-gray-600">/เดือน</span>
            </div>
            
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-600" />
                URL สั้นและจดจำง่าย
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-600" />
                ใช้ได้ทั้งคอร์สและ Salepage
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-600" />
                ราคาประหยัด
              </li>
            </ul>
          </div>

          <div
            onClick={() => setSelectedType('subdomain')}
            className={`bg-white rounded-2xl p-6 cursor-pointer transition-all ${
              selectedType === 'subdomain'
                ? 'ring-4 ring-emerald-500 shadow-xl'
                : 'hover:shadow-lg'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Subdomain</h3>
                <p className="text-sm text-gray-600">แนะนำ - มืออาชีพ</p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedType === 'subdomain'
                  ? 'border-emerald-500 bg-emerald-500'
                  : 'border-gray-300'
              }`}>
                {selectedType === 'subdomain' && <Check className="w-4 h-4 text-white" />}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 rounded-lg p-4 mb-4">
              <code className="text-sm font-semibold text-gray-700">
                username.edumi.co
              </code>
            </div>
            
            <div className="text-3xl font-bold text-gray-900 mb-4">
              99฿<span className="text-lg font-normal text-gray-600">/เดือน</span>
            </div>
            
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-600" />
                URL เป็นของคุณเอง
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-600" />
                ดูมืออาชีพและน่าเชื่อถือ
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-600" />
                SEO ดีกว่า Path URL
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-green-600" />
                ใช้ได้ทั้งคอร์สและ Salepage
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">เลือก Username ของคุณ</h2>
          
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              placeholder="username"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors"
            />
            
            {isCheckingUsername && (
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                <Loader className="w-4 h-4 animate-spin" />
                กำลังตรวจสอบ...
              </div>
            )}
            
            {usernameAvailable === true && (
              <div className="flex items-center gap-2 mt-2 text-sm text-green-600">
                <Check className="w-4 h-4" />
                Username นี้ใช้ได้!
              </div>
            )}
            
            {usernameAvailable === false && usernameError && (
              <div className="flex items-center gap-2 mt-2 text-sm text-red-600">
                <X className="w-4 h-4" />
                {usernameError}
              </div>
            )}
          </div>

          {username && usernameAvailable && (
            <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 border-2 border-emerald-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <Globe className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900 mb-2">URL ของคุณ:</p>
                  <p className="text-lg font-mono font-bold text-emerald-700 break-all">
                    https://{previewUrl}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-5 h-5" />
              <p className="font-semibold">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm text-gray-600">ราคา</p>
              <p className="text-3xl font-bold text-gray-900">
                {price}฿<span className="text-lg font-normal text-gray-600">/เดือน</span>
              </p>
            </div>
            <button
              onClick={handlePurchase}
              disabled={!username || !usernameAvailable || isPurchasing}
              className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isPurchasing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  กำลังดำเนินการ...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  ชำระเงิน {price}฿
                </>
              )}
            </button>
          </div>
          
          <p className="text-xs text-gray-500 text-center">
            * URL Add-on ใช้ได้ทั้งระบบคอร์สเรียนและ Salepage
          </p>
        </div>
      </div>
    </div>
  );
}
